import {
	Placement,
	ProgressBarSettings,
	Service,
	UIElement,
	UIElementType,
	ExperienceWorkflowWithContext,
	WorkflowId,
} from '@ply-io/ply-front-shared-utilities';
import { FlowRunOutput, FlowRunStatuses, NodeRunState } from '@ply-io/runner';
import { ComputedRef, InjectionKey } from 'vue';
import { ServiceContextMapIdKey } from '../types';
import { NonNullableProperties } from '@/utils/types';

export const TILE_ID_ADD_ON = 'add-on' as const;
export type TileId = WorkflowId | typeof TILE_ID_ADD_ON;

export interface AddOnTile extends TileWithWorkflow {
	element: UIElement;
	placement: Placement;
}

export interface Tile {
	id: TileId;
	title: string;
	children?: AddOnTile[];
	elementType?: UIElementType;
	workflow?: ExperienceWorkflowWithContext;
	isPublished?: boolean;
	context?: ServiceContextMapIdKey;
	service?: Service;
	icon?: string;
	isCreated?: boolean;
}

export type TileWithWorkflow = NonNullableProperties<Tile, 'elementType' | 'workflow' | 'isPublished' | 'context' | 'service'>;
export interface WorkflowStatusState {
	isShow: boolean;
	text: string;
	percentCompleted: string;
	runState: {
		nodeRuns: NodeRunState[];
		status: FlowRunStatuses;
	};
	errorParams?: {
		isStepError: boolean;
		msg: string;
		actionTitle: string;
		editStepUrl: string;
		serviceSlug: string;
		creatorWasNotified: string | null;
	};
}
export type FlowRunSettings = {
	favicon_enabled: boolean;
	favicon: string;
	page_title_enabled: boolean;
	page_title: string;
	should_hide_errors_from_organization_users: boolean;
	should_hide_errors_from_non_organization_users: boolean;
	progress_bar: ProgressBarSettings;
};

export const isFlowRunSettings = (settings: Record<string, unknown>): settings is FlowRunSettings =>
	'should_hide_errors_from_non_organization_users' in settings &&
	'should_hide_errors_from_organization_users' in settings &&
	'favicon_enabled' in settings &&
	'favicon' in settings &&
	'page_title_enabled' in settings &&
	'page_title' in settings &&
	'progress_bar' in settings;

export interface FlowRunAssets {
	status: ComputedRef<FlowRunStatuses>;
	nodeRuns: ComputedRef<NodeRunState[]>;
	percentCompleted: ComputedRef<number>;
	text: ComputedRef<string>;
	restart: () => void;
	isFinalized: ComputedRef<boolean>;
	endRunData: ComputedRef<FlowRunOutput>;
}

export const FlowRunAssetsKey: InjectionKey<FlowRunAssets> = Symbol('FlowRunAssetsKey');
export type FlowRunErrorAssets = {
	mainErrorMessage: ComputedRef<string>;
	isFailed: ComputedRef<boolean>;
	canSeeDetails: ComputedRef<boolean>;
	dismiss: () => void;
	showErrorDetails: () => void;
};

export const FlowRunErrorAssetsKey: InjectionKey<FlowRunErrorAssets> = Symbol('FlowRunErrorAssetsKey');

export interface ProgressBarMenu {
	progressBarMenu: ComputedRef<boolean>;
	setProgressBarMenu: (value: boolean) => void;
}

export const ProgressBarMenuKey: InjectionKey<ProgressBarMenu> = Symbol('ProgressBarMenuKey');
